import * as Popover from "@radix-ui/react-popover";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/cn";
import { seccionCoincide } from "@/lib/navegacion";
import type { GrupoNav } from "./AppShell";
import { Button } from "./Button";

export interface MenuUsuarioProps {
  nombre: string;
  /** Grupos del menú que van acá en vez de la barra (ej. Configuración), arriba de Mi perfil. */
  grupos?: readonly GrupoNav[];
  rutaPerfil: string;
  onSalir: () => void;
}

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

/** Menú del usuario en el header: nombre (iniciales en el celular) → los grupos que vengan, Mi perfil y Salir. */
export function MenuUsuario({ nombre, grupos = [], rutaPerfil, onSalir }: MenuUsuarioProps) {
  const [abierto, setAbierto] = useState(false);
  const { pathname } = useLocation();
  return (
    <Popover.Root open={abierto} onOpenChange={setAbierto}>
      <Popover.Trigger asChild>
        <Button variante="fantasma" tamanio="sm" aria-label={nombre}>
          <span aria-hidden="true" className="grid size-8 place-items-center rounded-full bg-primario-suave text-chico font-semibold text-primario sm:hidden">
            {iniciales(nombre)}
          </span>
          <span className="hidden sm:inline">{nombre}</span>
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 max-h-[var(--radix-popover-content-available-height)] min-w-48 overflow-y-auto rounded-tarjeta border border-borde bg-superficie p-1 shadow-flotante"
        >
          {grupos.map((g) => (
            <div key={g.id} role="group" aria-label={g.etiqueta} className="mb-1 border-b border-borde pb-1">
              <p aria-hidden="true" className="px-3 pb-1 pt-2 text-chico font-semibold uppercase tracking-wide text-texto-suave">
                {g.etiqueta}
              </p>
              {g.secciones.map((s) => {
                const activa = seccionCoincide(s, pathname);
                return (
                  <Link
                    key={s.ruta}
                    to={s.ruta}
                    onClick={() => setAbierto(false)}
                    aria-current={activa ? "page" : undefined}
                    className={cn("block rounded-control px-3 py-2", activa ? "bg-primario-suave font-medium text-primario" : "hover:bg-superficie-2")}
                  >
                    {s.etiqueta}
                  </Link>
                );
              })}
            </div>
          ))}
          <Link to={rutaPerfil} onClick={() => setAbierto(false)} className="block rounded-control px-3 py-2 hover:bg-superficie-2">
            Mi perfil
          </Link>
          <button type="button" onClick={onSalir} className="block w-full rounded-control px-3 py-2 text-left hover:bg-superficie-2">
            Salir
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
